<script lang="ts">
export default {
  name: "SearchResultsItemInner",
};
</script>

<script lang="ts" setup>
import type { SearchResult } from "@/services/fobizzPlatformApi";
import {
  Item,
  Material,
  Folder,
  AiAssistant,
  AppEnum,
} from "@/services/fobizzToolsApi";

const { d } = useI18n();

type Props = {
  result: Item | Material | Folder | SearchResult;
  gallery?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  gallery: false,
});

const color = computed(() => {
  if (
    !("kind" in props.result) ||
    (props.gallery && props.result.kind === "Collection")
  ) {
    return "#e9ecef";
  } else {
    return (props.result as Item).color;
  }
});

const icon = computed(() => {
  if (!("kind" in props.result)) {
    return "bi bi-folder";
  } else if (props.gallery && props.result.kind === "Collection") {
    return "bi bi-collection";
  } else {
    return (props.result as Item).icon;
  }
});

const isAssistant = computed(() => {
  return "kind" in props.result && props.result.kind === AppEnum.AI_ASSISTANT;
});

const hasImage = computed(() => {
  return isAssistant.value && !!(props.result as AiAssistant).image;
});
</script>

<template>
  <div class="result-item__start">
    <template v-if="!isAssistant">
      <div class="result-item__icon" :style="`background-color: ${color}`">
        <i :class="`${icon}`"></i>
      </div>
    </template>
    <template v-else>
      <div
        class="result-item__avatar"
        :class="{ 'result-item__avatar--no-image': !hasImage }"
      >
        <ChatAssistantAvatar :assistant="result as AiAssistant" size="sm" />
      </div>
    </template>
    <div class="result-item__name">
      {{ result.name }}
    </div>
  </div>
  <div class="result-item__end">
    <span v-if="result.createdAt" class="result-item__created-at">
      {{ d(result.createdAt, "numeric") }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
.result-item {
  &__start {
    display: flex;
    align-items: center;
    gap: $spacer-2;
    width: 70%;
    flex-wrap: nowrap;

    @include media-breakpoint-down(sm) {
      max-width: unset;
      width: 100%;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 36px;
    height: 36px;
    border-radius: $border-radius;
    color: $body-color;
    background-color: $gray-200;
    font-size: 1.2rem;
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;

    &--no-image {
      :deep(.assistant-avatar) {
        background-color: $tools-color-rose !important;
      }
    }
  }

  &__name {
    color: $color-black;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
  }

  &__end {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 30%;
    gap: $spacer-2;

    @include media-breakpoint-down(md) {
      justify-content: flex-end;
    }

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  &__created-at {
    width: fit-content;
    font-size: $font-size-xs;
    color: $secondary;
  }
}
</style>
